/** GDPR analytics consent — PostHog is the only consent-gated storage on
 * this site (Auth0 session, credits, language and the IndexedDB history are
 * strictly necessary / functional and stay outside the gate).
 *
 * A decision can come from two places, most specific first:
 *
 * 1. Our own record — localStorage `stt_consent`, written when the user
 *    answers the banner (or the footer "Cookie settings") here.
 * 2. Inherited from the main site: lingvanex.com and app.lingvanex.com run
 *    tarteaucitron and write its cookie on `.lingvanex.com`, so every
 *    subdomain can read it. Their service list includes `customPosthog` —
 *    exactly the tool this site loads — so that value is inherited verbatim:
 *    `true` → granted, `false` → denied, `wait`/absent → undecided.
 *
 * While no local record exists the inheritance is live: consenting (or
 * revoking) on the main site takes effect here on the next page load, and
 * the banner is never shown to a user who already answered there.
 * `unset` means "show the banner". */

export type ConsentDecision = 'granted' | 'denied' | 'unset'

const STORAGE_KEY = 'stt_consent'
// Bump to re-ask everyone after a material cookie-policy change
const CONSENT_VERSION = 1
// Re-ask after a year — same lifetime as the main site's tarteaucitron cookie
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000

interface StoredConsent {
  analytics: boolean
  ts: number
  v: number
}

function ownDecision(): ConsentDecision {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return 'unset'
    const stored = JSON.parse(raw) as StoredConsent
    if (stored.v !== CONSENT_VERSION) return 'unset'
    if (Date.now() - stored.ts > MAX_AGE_MS) return 'unset'
    return stored.analytics ? 'granted' : 'denied'
  } catch {
    return 'unset'
  }
}

function inheritedDecision(): ConsentDecision {
  try {
    const match = document.cookie.match(/(?:^|;\s*)tarteaucitron=([^;]*)/)
    if (!match) return 'unset'
    const value = decodeURIComponent(match[1])
    if (value.includes('customPosthog=true')) return 'granted'
    if (value.includes('customPosthog=false')) return 'denied'
    return 'unset'
  } catch {
    return 'unset'
  }
}

export function analyticsConsent(): ConsentDecision {
  const own = ownDecision()
  return own !== 'unset' ? own : inheritedDecision()
}

type ConsentListener = (granted: boolean) => void
const consentListeners = new Set<ConsentListener>()

/** Fires whenever the user makes/changes a decision in this tab. */
export function onConsentChange(fn: ConsentListener): () => void {
  consentListeners.add(fn)
  return () => consentListeners.delete(fn)
}

type OpenListener = () => void
const openListeners = new Set<OpenListener>()

/** Ask for the consent dialog. The floating revisit disc is desktop-only —
 * on a phone a 42 px opaque circle parks itself over whatever is bottom-left,
 * and on the pricing grid that is the corner of the card wearing the
 * "Popular" badge. Withdrawing consent still has to be reachable at every
 * width (GDPR), so narrow screens reach it through the footer instead. */
export function openConsentSettings() {
  for (const fn of openListeners) fn()
}

export function onOpenConsentSettings(fn: OpenListener): () => void {
  openListeners.add(fn)
  return () => openListeners.delete(fn)
}

export function saveAnalyticsConsent(granted: boolean) {
  try {
    const record: StoredConsent = { analytics: granted, ts: Date.now(), v: CONSENT_VERSION }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // storage unavailable (private mode) — the decision still applies to this page view
  }
  for (const fn of consentListeners) fn(granted)
}

